import React from "react";
import { Outlet, useLocation } from "react-router";
import SelectModeBannerOpener from "../../../Components/Banners/SelectModeBanner/opener";
import SettingsUsersSelectModeBanner from "../../../Components/Banners/SettingsUsersSelectModeBanner/settings-users-select-mode-banner";
import { AclTypes } from "../../../Core/Api/Types/acl-types";

import DashboardLayout from "../../../Layouts/Dashboard/dashboard";
import { routePaths } from "../../../Router/router";
import { t } from "i18next";
import KalikoHeader from "./SettingsDiagramDesigner/kaliko-header";

export type SettingsOutletContextType = {
  selectionMode: {
    type: "user" | "roles-and-permissions" | string;
    selection: number[];
  };

  setSelectionMode: React.Dispatch<React.SetStateAction<SettingsOutletContextType["selectionMode"]>>;
};

export enum SettingsGroups {
  broadcasters = "broadcasters",
  users = "users",
  orders = "orders",
  inventory = "inventory",
  system = "rules",
  platform = "platform",
}

export const settingsGroupsDetails = () => ({
  [SettingsGroups.broadcasters]: [
    {
      path: routePaths.settingsBroadcasters.path,
      label: t("common:broadcasters"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclSectionBackofficeAccess],
    },
    {
      path: routePaths.settingsExportSessions.path,
      label: t("common:exportSessions"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclMetadataExport],
    },
    {
      path: routePaths.AdminSettingsYoutubeChannels.path,
      label: t("common:youtubeChannels"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclBackofficeYoutubeChannel],
    },
  ],
  [SettingsGroups.users]: [
    {
      path: routePaths.settingsUsers.path,
      label: t("common:users"),
      acls: [AclTypes.aclBackofficeUserListView],
    },
    {
      path: routePaths.settingsRolesAndPermissions.path,
      label: t("common:rolesAndPermissions"),
      acls: [AclTypes.aclBackofficeUserListView],
    },
    {
      path: routePaths.settingsPlanningResources.path,
      label: t("common:planningResources"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsMailingList.path,
      label: t("field:placeholder.mailingList"),
      acls: [AclTypes.aclBackofficeMailinglistView, AclTypes.aclBackofficeMailinglistCreate],
    },
  ],
  [SettingsGroups.orders]: [
    {
      path: routePaths.settingsOrders.path,
      label: t("route:linkTitle.basketProducts"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclSectionBackofficeAccess, AclTypes.aclBackofficeDeliveryProductView],
    },
    {
      path: routePaths.settingsPriceRulesManagement.path,
      label: t("common:priceRules"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclOrderPriceRuleView],
    },
    {
      path: routePaths.settingsManualTasks.path,
      label: t("common:manualTasks"),
      acls: [AclTypes.aclBackofficeManualTasksCreate],
    },
  ],
  [SettingsGroups.inventory]: [
    {
      path: routePaths.settingsInventory.path,
      label: t("common:metadataFields"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsLegacyImporter.path,
      label: t("common:legacyImporter"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclAdmin],
    },
    {
      path: routePaths.settingsBulkMetaImporter.path,
      label: t("route:linkTitle.bulkMetadataImporter"),
      acls: [AclTypes.aclImportMetadataBulk],
    },
    {
      path: routePaths.settingsMetadataViews.path,
      label: t("common:metadataViews"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsFormats.path,
      label: t("common:formats"),
      acls: [AclTypes.aclSuperUser],
    },
  ],
  [SettingsGroups.system]: [
    {
      path: routePaths.settingsSystem.path,
      label: t("common:jobs"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsJobRules.path,
      label: t("common:jobRules"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsPurgeRules.path,
      label: t("common:purgeRules"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsWorkflowModels.path,
      label: t("common:workflowsModels"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsWorkflowDesigner.path,
      label: t("common:workflowsDesigner"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsStatusTransitions.path,
      label: t("common:statusTransitions"),
      acls: [AclTypes.aclSuperUser],
    },
  ],
  [SettingsGroups.platform]: [
    {
      path: routePaths.settingsGlobal.path,
      label: t("common:global"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsSystemAlerts.path,
      label: t("route:linkTitle.systemAlerts"),
      acls: [AclTypes.aclSuperUser, AclTypes.aclSectionBackofficeAccess],
    },
    {
      path: routePaths.settingsCopyPlatform.path,
      label: t("common:platformCopy"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsStorages.path,
      label: t("common:storages"),
      acls: [AclTypes.aclSuperUser],
    },
    {
      path: routePaths.settingsAgentsAndLicenses.path,
      label: t("route:linkTitle.agentsAndLicences"),
      acls: [AclTypes.aclSuperUser],
    },
  ],
});

function Settings() {
  const [selectionMode, setSelectionMode] = React.useState<SettingsOutletContextType["selectionMode"]>({
    type: "",
    selection: [],
  });
  const location = useLocation();

  const onCloseBanner = () => {
    setSelectionMode({ type: "", selection: [] });
  };

  const showKalikoHeader = location.pathname.includes(routePaths.settingsWorkflowDesigner.path);

  return (
    <DashboardLayout
      sxContent={{ px: 2 }}
      showBanner={false}
      noScroll
      maxWidth={false}
      Heading={showKalikoHeader && <KalikoHeader />}
      panelHighlight={!showKalikoHeader}
      Banner={
        <SettingsUsersSelectModeBanner
          selection={selectionMode.selection}
          context={selectionMode.type}
          handleCloseClick={onCloseBanner}
        />
      }
    >
      <SelectModeBannerOpener selectedObjects={selectionMode.selection} />
      <Outlet context={{ selectionMode, setSelectionMode }} />
    </DashboardLayout>
  );
}

export default Settings;
